import React, {
  CSSProperties,
  HTMLProps,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { Carousel } from "react-responsive-carousel";
import { SubmitImageSlot, DirectBackendReqType } from "sockapi/direct-backend";
import { clsObj } from "client-util/util";
import settings from "settings";
import {
  useSeparatelyPersistedState,
  useUpdateSeparatelyPersistedState,
} from "stores/UserPreferencesStore";

import {
  useAccessibleRounds,
  useAccessiblePuzzles,
} from "stores/ScopeStateStore";
import { useSockClient } from "stores/ServerInteractionStore";
import { Spinner } from "components/Loading";
import PuzzleContentDisplay from "components/PuzzleContent/PuzzleContentDisplay";
import {
  BASE_FONT_SIZE_PX,
  BASE_OUTER_WRAPPER_WIDTH_PX,
  FONT_SIZE_SCALING_PER_ROUND,
} from "components/PuzzlePage/PuzzleBodyDisplay";
import { useUILock } from "client-util/useUILock";
import { PuzzleLink } from "components/Link";

export function Flavor(props: { children: string }) {
  return (
    <div className="flavor">
      <i>{props.children}</i>
    </div>
  );
}

type PuzzleImageProps = {
  staticPuzDir: string;
  whiteBackground?: boolean;
  pad?: boolean;
  fullWidth?: boolean;
  fullHeight?: boolean;
  fullPiece?: boolean;
  block?: boolean;
  inverted?: boolean;
  center?: boolean;
  maxWidth?: string;
  width?: string;
  name?: string;
  nameWithExt?: string;
  ext?: string;
  unscoped?: boolean;
  alt?: string;
  style?: CSSProperties /** Style for the div around the image. */;
  className?: string /** Additional classes. */;
  dir?: "left" | "right" | "top" | "bottom";
  version?: number /** Bump this to bypass cache when updating images. */;
  caption?: ReactNode;
};

export function PuzzleImage(props: PuzzleImageProps) {
  const {
    staticPuzDir,
    whiteBackground = false,
    pad = false,
    fullWidth = false,
    fullHeight = false,
    fullPiece = false,
    block = false,
    inverted = false,
    center = false,
    maxWidth,
    width,
    unscoped = false,
    alt,
    style,
    className,
    dir,
    version = 0,
    caption,
  } = props;

  const nameWithExt =
    props.nameWithExt ??
    (props.name !== undefined ? `${props.name}.png` : undefined);

  return (
    <div
      className={`${clsObj({
        "puz-img": true,
        "puz-img-center": center,
        [`puz-img-${dir}`]: !!dir,
      })}${className ? ` ${className}` : ""}`}
      style={style}
    >
      <img
        className={clsObj({
          "puz-img-white-bg": whiteBackground,
          "puz-img-pad": pad,
          "puz-img-full-width": fullWidth,
          "puz-img-full-height": fullHeight,
          "puz-img-full-piece": fullPiece,
          "puz-img-block": block,
          "puz-img-inverted": inverted,
        })}
        style={{
          maxWidth:
            maxWidth !== undefined ? `min(100%, ${maxWidth})` : undefined,
          width: width !== undefined ? `min(100%, ${width})` : undefined,
        }}
        src={`${
          nameWithExt === undefined
            ? `${staticPuzDir}/default.${props.ext ?? "png"}`
            : unscoped
              ? `${settings.staticImagesDir}/${nameWithExt}`
              : `${staticPuzDir}/default/${nameWithExt}`
        }?${version}`}
        alt={alt === undefined ? "" : alt}
        title={alt === undefined ? "" : alt}
      />
      {caption && <p className="puz-img-caption">{caption}</p>}
    </div>
  );
}

type GalleryImageSpec = {
  images?: string[];
  caption?: ReactNode;
  halfwidth?: boolean;
  thirdwidth?: boolean;
};

export function PuzzleGallery(props: {
  staticPuzDir: string;
  items: GalleryImageSpec[];
  animationHandler?: "slide" | "fade";
  imgProps?: PuzzleImageProps;
  carouselInnerStyle?: CSSProperties;
}) {
  const {
    staticPuzDir,
    items,
    animationHandler,
    imgProps,
    carouselInnerStyle,
  } = props;

  return (
    <Carousel
      showStatus={false}
      showThumbs={false}
      animationHandler={animationHandler}
      swipeable={
        // Upstream explicitly does not support swiping with the
        // fade animation handler.
        animationHandler !== "fade"
      }
    >
      {items.map(
        ({ images, caption, halfwidth = false, thirdwidth = false }, i) => (
          <div key={i} style={{ height: "100%" }}>
            <div className="carousel-inner" style={carouselInnerStyle}>
              {(images ?? []).map((image) => (
                <PuzzleImage
                  key={image}
                  staticPuzDir={staticPuzDir}
                  style={{
                    width: halfwidth ? "50%" : thirdwidth ? "33%" : undefined,
                  }}
                  {...imgProps}
                  {...(imgProps?.nameWithExt
                    ? {
                        nameWithExt: image,
                      }
                    : {
                        name: image,
                      })}
                />
              ))}
            </div>
            {!caption ? null : (
              <p className="legend">
                {typeof caption === "string"
                  ? caption.split("\n").map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))
                  : caption}
              </p>
            )}
          </div>
        )
      )}
    </Carousel>
  );
}

export function PuzzleAudio(
  props: {
    staticPuzDir: string;
    name?: string;
    isPreview: boolean;
  } & HTMLProps<HTMLAudioElement>
) {
  const {
    staticPuzDir,
    name,
    autoPlay: autoPlayProp,
    isPreview,
    ...otherProps
  } = props;
  const autoPlay = (autoPlayProp ?? false) && !isPreview;
  const audioSrc =
    name === undefined
      ? `${staticPuzDir}/default.mp3`
      : `${staticPuzDir}/default/${name}.mp3`;
  const audioRef = useRef<HTMLAudioElement>(null);
  const autoplayIsPaused = useSeparatelyPersistedState(
    (state) => state.autoplayIsPaused
  );
  const autoplayVolume = useSeparatelyPersistedState(
    (state) => state.autoplayVolume
  );
  const updateSeparatelyPersistedState = useUpdateSeparatelyPersistedState();

  useEffect(() => {
    const audioElem = audioRef.current;
    if (audioElem === null) return;
    if (!autoPlay) return;
    audioElem.volume = autoplayVolume ?? 0.3;
    const onPause = () => {
      updateSeparatelyPersistedState({ autoplayIsPaused: true });
    };
    const onVolumeChange = () => {
      updateSeparatelyPersistedState({ autoplayVolume: audioElem.volume });
    };
    // Note that this also triggers if you navigate away from the page,
    // so we only ever autoplay once.
    audioElem.addEventListener("pause", onPause);
    audioElem.addEventListener("volumechange", onVolumeChange);
    return () => {
      audioElem.removeEventListener("pause", onPause);
      audioElem.removeEventListener("volumechange", onVolumeChange);
    };
  }, [autoPlay, autoplayVolume, updateSeparatelyPersistedState]);

  return (
    <div className="puz-audio">
      <span className="copy-only">
        <a href={audioSrc}>[See original puzzle for audio]</a>
      </span>
      <audio
        ref={audioRef}
        controls
        src={audioSrc}
        autoPlay={autoPlay && !autoplayIsPaused}
        {...otherProps}
      />
    </div>
  );
}

/**
 * As in: _ _ [_] _ _ _
 * Or: _ 1 _  _ [2] _
 *
 * Have only two spaces between words!
 */
export function Blanks(props: {
  content: string;
  lessLowerMargin?: boolean;
  inline?: boolean;
}) {
  const { content, lessLowerMargin = false, inline = false } = props;
  const linesDom = content
    .trim()
    .split("\n")
    .map((line, lineNum) => (
      <span key={lineNum} className="blanks">
        {line.split(" ").map((blank, i) => {
          const boxed = blank.startsWith("[") && blank.endsWith("]");
          const content = boxed ? blank.slice(1, -1) : blank;
          const space = content === "" || /[^_\d]/.test(content);
          const child =
            content === "_" ? (
              ""
            ) : content.length > 1 ? (
              <span style={{ fontSize: 160 / content.length + "%" }}>
                {content}
              </span>
            ) : (
              content
            );

          return (
            <span key={i} className={clsObj({ boxed, space })}>
              {child}
            </span>
          );
        })}
      </span>
    ));

  // HACK: Just replace double space with a bunch of spaces. A more
  // robust solution would be to generate a larger number of spaces
  // depending on the number of spaces found in a row.
  const copyjackContent = [...content.replaceAll("  ", "    ")].map((c, i) => (
    <span key={i}>{c === " " ? <>&nbsp;</> : c === "\n" ? <br /> : c}</span>
  ));
  const wrapperClassName = clsObj({
    "blanks-wrapper": true,
    "less-lower-margin": lessLowerMargin,
    "no-copy": true,
    "blanks-wrapper-inline": inline,
  });

  return (
    <>
      {inline ? (
        <>
          <span className="copy-only">{copyjackContent}</span>
          <span className={wrapperClassName}>{linesDom}</span>
        </>
      ) : (
        <>
          <div className="copy-only">{copyjackContent}</div>
          <div className={wrapperClassName}>{linesDom}</div>
        </>
      )}
    </>
  );
}

export function Table(props: {
  headers?: ReactNode[];
  cells: (
    | ReactNode
    | ({ data: ReactNode } & HTMLProps<HTMLTableCellElement>)
  )[][];
  tableClassName?: string;
  tableStyle?: CSSProperties;
  headerStyle?: CSSProperties;
  cellStyle?: CSSProperties;
}) {
  const { headers, cells, tableClassName, tableStyle, headerStyle, cellStyle } =
    props;
  return (
    <table className={tableClassName} style={tableStyle}>
      <tbody>
        {!!headers && (
          <tr>
            {headers.map((cell, i) => (
              <th key={i} style={headerStyle}>
                {cell}
              </th>
            ))}
          </tr>
        )}
        {cells.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => {
              if (
                typeof cell !== "object" ||
                cell === null ||
                !("data" in cell)
              ) {
                return (
                  <td key={j} style={cellStyle}>
                    {cell}
                  </td>
                );
              }
              const { data, style, ...otherProps } = cell;
              return (
                <td key={j} style={{ ...cellStyle, ...style }} {...otherProps}>
                  {data}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function SpoilerText(props: { children: ReactNode }) {
  return <span className="solved-title-answer spoiler">{props.children}</span>;
}

export function AuthorsNotes(props: { children: string }) {
  return (
    <>
      <h4>Authors&rsquo; Notes</h4>
      {props.children}
    </>
  );
}

export function SolutionPieceGrid(
  props: {
    widthPx: number;
    cols: number;
    rows: number;
    pieces: {
      [puzName: string]: {
        x: number;
        y: number;
        colspan?: number;
        rowspan?: number;
        rotate?: number;
        flip?: boolean;
      };
    };
  } & HTMLProps<HTMLDivElement>
) {
  const rounds = useAccessibleRounds();
  const puzzles = useAccessiblePuzzles();
  if (rounds === null || puzzles === null) return <Spinner />;

  const { widthPx, cols, rows, pieces, style = {}, ...otherProps } = props;
  const pieceViews = Object.entries(pieces).map(([puzName, piece]) => {
    const puzData = puzzles[puzName];
    if (puzData === undefined) return null;
    const { x, y, colspan = 1, rowspan = 1, rotate = 0, flip = false } = piece;
    const pieceCols =
      puzData.cols ??
      (puzData.roundId === undefined
        ? null
        : rounds[puzData.roundId]?.pieceCols) ??
      1;
    return (
      <PuzzleLink key={puzName} puzName={puzName} subpage="piece">
        <div
          className={clsObj({
            "piece-view": true,
            "piece-view-transparent": !!puzData.transparentBackground,
          })}
          style={{
            left: `${(100 * x) / cols}%`,
            top: `${(100 * y) / rows}%`,
            width: `${(100 * colspan) / cols}%`,
            height: `${(100 * rowspan) / rows}%`,
            rotate: `${rotate}deg`,
            scale: flip ? "-1 1" : undefined,
          }}
        >
          <PuzzleContentDisplay
            puzName={puzName}
            fontSize={`${(BASE_FONT_SIZE_PX / pieceCols) * (widthPx / BASE_OUTER_WRAPPER_WIDTH_PX) * (colspan / cols) * (FONT_SIZE_SCALING_PER_ROUND[puzData.roundId ?? ""] ?? 1)}px`}
          />
        </div>
      </PuzzleLink>
    );
  });
  return (
    <div
      style={{
        aspectRatio: `${cols} / ${rows}`,
        maxWidth: widthPx,
        margin: "1em auto",
        position: "relative",
        ...style,
      }}
      {...otherProps}
    >
      {pieceViews}
    </div>
  );
}

export function ImageSubmission(props: {
  isLocked: boolean;
  slot: SubmitImageSlot;
  supportedMimeTypes: string[];
  maxFileSize: number;
  onSubmitSuccess: () => Promise<void>;
}) {
  const { isLocked, slot, supportedMimeTypes, maxFileSize, onSubmitSuccess } =
    props;
  const [fileErr, setFileErr] = useState<string | null>(null);
  const sockClient = useSockClient();
  const uploadLock = useUILock();

  return (
    <div className="image-submission">
      <div>
        <input
          type="file"
          disabled={isLocked || uploadLock.isHeld}
          onChange={(e) => {
            if (e.target.files === null) return;
            const [file] = e.target.files;
            if (file === undefined) return;
            if (!supportedMimeTypes.includes(file.type)) {
              setFileErr("File format not supported.");
              return;
            }
            if (file.size > maxFileSize) {
              setFileErr("File is too large.");
              return;
            }
            uploadLock.acquireAndRun(async (getIsCanceled) => {
              try {
                await sockClient.sendDirectBackendReqAsync(
                  {
                    type: DirectBackendReqType.SUBMIT_IMAGE,
                    payload: { slot },
                  },
                  file,
                  () =>
                    new Promise((resolve, reject) => {
                      const reader = new FileReader();
                      reader.onload = (e) => {
                        const url = e.target?.result;
                        if (typeof url !== "string") {
                          reject(new Error("failed to read file"));
                          return;
                        }
                        resolve(url);
                      };
                      reader.onerror = (e) => {
                        reject(e.target?.error);
                      };
                      reader.readAsDataURL(file);
                    })
                );
                await onSubmitSuccess();
                setFileErr(null);
              } catch (err) {
                setFileErr(
                  "Failed to upload image. Please try again later and contact us if the problem persists."
                );
                console.error(err);
              }
            });
          }}
        />
      </div>
      {uploadLock.isHeld ? (
        <Spinner inline />
      ) : fileErr === null ? null : (
        <div className="image-submission-error">{fileErr}</div>
      )}
    </div>
  );
}
